import type { ReactiveController, ReactiveControllerHost } from 'lit'
import type { KeybindingsMap } from 'tinykeys'
import { tinykeys } from 'tinykeys'

type ShortcutTarget = Parameters<typeof tinykeys>[0]

const ARROW_RE = /Arrow(Up|Down|Left|Right)/g
const ESC_RE = /Escape/g

function normalise(shortcuts: KeybindingsMap) {
  const normalised: KeybindingsMap = {}

  Object.keys(shortcuts).forEach((keybinding) => {
    normalised[keybinding] = shortcuts[keybinding]

    // Arrow and Escape keys in IE/Edge have different names
    if (ARROW_RE.test(keybinding)) {
      const mapped = keybinding.replace(ARROW_RE, (_match, capture) => capture)
      normalised[mapped] = normalised[keybinding]
    }
    if (ESC_RE.test(keybinding)) {
      const mapped = keybinding.replace(ESC_RE, () => 'Esc')
      normalised[mapped] = normalised[keybinding]
    }
  })

  return normalised
}

export class ShortcutController implements ReactiveController {
  private unregister?: ReturnType<typeof tinykeys>

  constructor(
    host: ReactiveControllerHost,
    private shortcuts?: KeybindingsMap,
    private target?: ShortcutTarget,
  ) {
    host.addController(this)
  }

  hostConnected() {
    if (this.shortcuts) {
      this.bind(this.shortcuts)
    }
  }

  hostDisconnected() {
    this.unbind()
  }

  unbind() {
    this.unregister?.()
  }

  bind(shortcuts: KeybindingsMap) {
    this.unbind()
    this.shortcuts = shortcuts
    // tinykeys v4 added a default `ignore` filter that drops key repeats and
    // events originating from form/contenteditable elements. Our components
    // bind shortcuts to `window`/host but rely on them firing while focus is in
    // an input (e.g. Escape to dismiss, `$mod+k`, command-menu arrow nav) and
    // on key repeat, so we opt out to preserve the pre-v4 behaviour. Per-command
    // "don't fire while typing" is handled in KeyboardController instead.
    this.unregister = tinykeys(this.target ?? window, normalise(this.shortcuts), { ignore: () => false })
  }
}

export type { KeybindingsMap as ShortcutMap }
